import { Tooltip } from '@hive/component/ui/tooltip';
import { SubscriptionPlan } from '@hive/graphql';
import { useI18n } from '@hive/i18n';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import { type SyntheticEvent, useEffect } from 'react';

import {
  ServerService,
  SubscriptionService,
  UserQuotaService,
  WorkspaceSubscriptionService,
} from '../../../modules/cloud';
import { WorkspaceService } from '../../../modules/workspace';
import * as styles from './style.css';

export const UserPlanButton = ({
  onClick,
}: {
  onClick: (e: SyntheticEvent<Element, Event>) => void;
}) => {
  const serverService = useService(ServerService);
  const subscriptionService = useService(SubscriptionService);
  const userQuotaService = useService(UserQuotaService);
  // WorkspaceService is optional since this component may be used outside workspace context
  const workspaceService = useServiceOptional(WorkspaceService);
  const workspaceSubscriptionService = useServiceOptional(
    WorkspaceSubscriptionService
  );

  const hasPayment = useLiveData(
    serverService.server.features$.map(r => r?.payment)
  );

  // Check user subscription (for personal plans)
  const userPlan = useLiveData(
    subscriptionService.subscription.pro$.map(subscription =>
      subscription !== null ? subscription?.plan : null
    )
  );
  const isBeliever = useLiveData(subscriptionService.subscription.isBeliever$);

  // Check if user has a lifetime plan from quota
  const isLifetimePlan = useLiveData(userQuotaService.quota.isLifetimePlan$);
  const quotaPlanName = useLiveData(userQuotaService.quota.planName$);

  // Check workspace subscription (for team plans)
  const workspaceSubscription = useLiveData(
    workspaceSubscriptionService?.subscription.subscription$
  );
  const isTeamWorkspace = workspaceSubscription?.plan === SubscriptionPlan.Team;

  const isLoading = userPlan === null && quotaPlanName === null;

  useEffect(() => {
    // revalidate subscription to get the latest status
    subscriptionService.subscription.revalidate();
    workspaceSubscriptionService?.subscription.revalidate();
    userQuotaService.quota.revalidate();
  }, [subscriptionService, workspaceSubscriptionService, userQuotaService]);

  const t = useI18n();

  if (!hasPayment) {
    // no payment feature
    return;
  }

  if (isLoading) {
    // loading, do nothing
    return;
  }

  // Priority: Believer > Lifetime plan > Team workspace > User plan > Free
  const planLabel = isBeliever
    ? 'Believer'
    : isLifetimePlan
      ? quotaPlanName
      : isTeamWorkspace
        ? SubscriptionPlan.Team
        : (userPlan ?? SubscriptionPlan.Free);

  return (
    <Tooltip content={t['com.hive.payment.tag-tooltips']()} side="top">
      <div
        data-is-believer={isBeliever ? 'true' : undefined}
        data-is-lifetime={isLifetimePlan ? 'true' : undefined}
        data-is-team={isTeamWorkspace ? 'true' : undefined}
        className={styles.userPlanButton}
        onClick={onClick}
        data-event-props="$.settingsPanel.profileAndBadge.viewPlans"
      >
        {planLabel}
      </div>
    </Tooltip>
  );
};
